import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root'
})
export class PdfBuilderUtilitiesService {

  constructor() { }

  formatAge(reportDate: string, birthDate: string): string {
    if (!reportDate || !birthDate) return 'Desconocido';
  
    const birth = new Date(birthDate);
    const report = new Date(reportDate);
    let years = report.getFullYear() - birth.getFullYear();
    let months = report.getMonth() - birth.getMonth();
  
    if (months < 0) {
      years--;
      months += 12;
    }
  
    return `${years}a ${months}m`;
  }
  
  formatDate(date: string, options?: { hideTime?: boolean }): string {
    if (!date) return 'Desconocido';
  
    const dt = new Date(date + 'Z');
  
    const baseFormat: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    };
  
    const fullFormat: Intl.DateTimeFormatOptions = {
      ...baseFormat,
      hour: '2-digit',
      minute: '2-digit',
    };
  
    const format = options?.hideTime ? baseFormat : fullFormat;
  
    return dt.toLocaleString('es-PE', format);
  }

  // createSignature(imageDataSignature: any, maxWidth: number = 50, maxHeight: number = 25): Promise<{ img: HTMLImageElement, width: number, height: number }> {
  //   return new Promise((resolve, reject) => {
  //     const img = new Image();
  
  //     if (imageDataSignature != null) {
  //       img.src = imageDataSignature;
  
  //       img.onload = () => {
  //         const originalWidth = img.width;
  //         const originalHeight = img.height;
  
  //         // Calculate aspect ratio
  //         const aspectRatio = originalWidth / originalHeight;
  
  //         let scaledWidth = maxWidth;
  //         let scaledHeight = maxHeight;
  
  //         // Adjust the width and height to maintain the aspect ratio
  //         if (originalWidth > originalHeight) {
  //           // Landscape image, scale based on width
  //           scaledHeight = scaledWidth / aspectRatio;
  //         } else {
  //           // Portrait image, scale based on height
  //           scaledWidth = scaledHeight * aspectRatio;
  //         }
  
  //         // Ensure the scaled dimensions fit within both maxWidth and maxHeight
  //         if (scaledWidth > maxWidth) {
  //           scaledWidth = maxWidth;
  //           scaledHeight = maxWidth / aspectRatio;
  //         }
  //         if (scaledHeight > maxHeight) {
  //           scaledHeight = maxHeight;
  //           scaledWidth = maxHeight * aspectRatio;
  //         }
  
  //         resolve({ img, width: scaledWidth, height: scaledHeight });
  //       };
  
  //       img.onerror = (err) => {
  //         reject(err);
  //       };
  //     } else {
  //       reject(new Error('No image data'));
  //     }
  //   });
  // }

  createSignature(
    imageDataSignature: string,
    targetHeight: number = 20,   // desired height in PDF units
    maxWidth: number = 80,       // hard cap so it doesn’t overflow
    minHeight: number = 12,      // don’t let it go smaller than this
    minWidth: number = 50
  ): Promise<{ img: HTMLImageElement; width: number; height: number }> {
    return new Promise((resolve, reject) => {
      const img = new Image();

      if (!imageDataSignature) {
        reject(new Error('No image data'));
        return;
      }

      img.src = imageDataSignature;

      img.onload = () => {
        const originalWidth = img.width;
        const originalHeight = img.height;

        if (!originalWidth || !originalHeight) {
          reject(new Error('Invalid image dimensions'));
          return;
        }

        const aspectRatio = originalWidth / originalHeight;

        // 1) Start from desired height
        let height = targetHeight;
        let width = height * aspectRatio;

        // 2) If too wide, scale down to maxWidth
        if (width > maxWidth) {
          const scale = maxWidth / width;
          width = maxWidth;
          height = height * scale;
        }

        // 3) If now too small vertically, bump up (even if it slightly exceeds maxWidth)
        if (height < minHeight) {
          const scale = minHeight / height;
          height = minHeight;
          width = width * scale;
        }

        // 4) Enforce minimum width (helps for square-ish signatures)
        if (width < minWidth) {
          const scale = minWidth / width;
          width = minWidth;
          height = height * scale; // may go above targetHeight, which is OK
          // you can clamp again to maxHeight if you introduce it
        }

        resolve({ img, width, height });
      };

      img.onerror = (err) => reject(err);
    });
  }

  createDebugProxy<T extends object>(target: T, label: string = ''): T {
    const IS_DEV = !environment.production;

    if (!IS_DEV) return target;

    return new Proxy(target, {
      get: (target, prop: string) => {
        if (!(prop in target)) {
          console.warn(`[WARNING] Property '${prop}' does not exist on ${label || 'object'}.`);
          return '';
        }

        const value = target[prop as keyof T];
        return value && typeof value === 'object' && !Array.isArray(value)
          ? this.createDebugProxy(value as any, `${label}.${prop}`)
          : value;
      }
    });
  }

  public toPlainObject<T>(obj: T): T {
    const seen = new WeakSet();
  
    const recursiveCopy = (source: any): any => {
      if (source === null || typeof source !== 'object') {
        return source;
      }
  
      if (seen.has(source)) {
        return '[Circular]';
      }
  
      seen.add(source);
  
      const result: any = Array.isArray(source) ? [] : {};
  
      for (const key of Object.keys(source)) {
        const value = source[key];
        result[key] =
          value && typeof value === 'object' ? recursiveCopy(value) : value;
      }
  
      return result;
    };
  
    return recursiveCopy(obj);
  }
}
